<template>
	<div class="organization-appearance">
		<!-- Skeleton Loader -->
		<template v-if="loading">
			<!-- Logo Icon -->
			<section class="mb-5">
				<div class="organization-appearance__skeleton-logoicon-heading skeleton mb-4"></div>
				<div class="organization-appearance__skeleton-logoicon skeleton"></div>
			</section>
			<!-- Colors -->
			<section class="mb-5">
				<div class="organization-appearance__skeleton-colors-heading skeleton mb-4"></div>
				<div class="flex justify-between" :class="{'flex-col': isMobile}">
					<div :class="{'flex': isMobile}">
						<div>
							<div class="flex mt-4 items-center">
								<div class="organization-appearance__skeleton-swatch skeleton"></div><span class="organization-appearance__skeleton-swatch-text skeleton ml-3"></span>
							</div>
							<div class="flex mt-4 items-center">
								<div class="organization-appearance__skeleton-swatch skeleton"></div><span class="organization-appearance__skeleton-swatch-bg skeleton ml-3"></span>
							</div>
							<div class="flex mt-4 items-center">
								<div class="organization-appearance__skeleton-swatch skeleton"></div><span class="organization-appearance__skeleton-swatch-primary skeleton ml-3"></span>
							</div>
						</div>
						<div :class="{'ml-4': isMobile}">
							<div class="flex mt-4 items-center">
								<div class="organization-appearance__skeleton-swatch skeleton"></div><span class="organization-appearance__skeleton-swatch-secondary skeleton ml-3"></span>
							</div>
							<div class="flex mt-4 items-center">
								<div class="organization-appearance__skeleton-swatch skeleton"></div><span class="organization-appearance__skeleton-swatch-important skeleton ml-3"></span>
							</div>
						</div>
					</div>
					<div class="mt-4">
						<div class="organization-appearance__skeleton-thumbnail skeleton"></div>
					</div>
				</div>
				<!-- Color Actions -->
				<div class="flex mt-4">
					<div v-if="!isDefault" class="organization-appearance__skeleton-button skeleton"></div>
					<div class="ml-auto">
						<div class="organization-appearance__skeleton-button skeleton"></div>
					</div>
				</div>
			</section>
			<!-- Header Typeface -->
			<section class="pb-5">
				<div class="organization-appearance__skeleton-typeface-heading skeleton mb-4"></div>
				<div class="flex">
					<div class="organization-appearance__skeleton-checkbox skeleton"></div>
					<div class="organization-appearance__skeleton-checkbox-label skeleton ml-3"></div>
				</div>
			</section>
		</template>
		<!-- End Skeleton -->
		<template v-else>
			<CCallout v-if="showUpsell" type="info" class="mb-5">
				{{ upsellText }}
				<template v-if="isOrgOwner" #button>
					<RouterLink
						:to="`/${globalStore.organization.Code}/settings/selectplan?source=upgrade-now&details=appearance`"
						class="button button--primary"
					>
						Upgrade
					</RouterLink>
				</template>
			</CCallout>
			<section class="mb-5">
				<h2 class="font-display mb-4">Logo Icon</h2>
				<OrganizationLogoUpload
					:url="globalStore.orgAppearanceSettings?.LogoLink"
					:disabled="isLogoDisabled"
					:show-upsell="showUpsell"
					@show-upsell="showUpsellDialog = true"
				/>
			</section>
			<section class="mb-5">
				<h2 class="font-display mb-4">Colors</h2>
				<CCallout v-if="!isACompliantText" type="error">
					<p>
						The background color needs more contrast with the text color.
					</p>
				</CCallout>
				<CCallout v-else-if="!isAACompliantText" type="warning">
					<p>
						Consider adding more contrast between the background and the text color.
					</p>
				</CCallout>
				<div class="b4:flex flex-row justify-between items-center gap-5">
					<div class="organization-appearance__swatches b4:flex flex-col flex-shrink-0 columns-2 b5:columns-auto whitespace-nowrap">
						<div class="flex items-center flex-shrink-0">
							<CColorPicker
								:color="textColor"
								:allow-opacity="false"
								:use-buttons="false"
								:color-library="[]"
								heading="Text"
								placement="bottom"
								emit-format="hex"
								@change="color => { textColor = color; hasUnsavedColorChanges = true }"
							/>
							<span class="ml-2 font-bold">Text</span>
						</div>
						<div class="flex items-center flex-shrink-0">
							<CColorPicker
								:color="backgroundColor"
								:allow-opacity="false"
								:use-buttons="false"
								:color-library="[]"
								heading="Background"
								placement="bottom"
								emit-format="hex"
								@change="color => { backgroundColor = color; hasUnsavedColorChanges = true }"
							/>
							<span class="ml-2 font-bold">Background</span>
						</div>
						<div class="flex items-center flex-shrink-0">
							<CColorPicker
								:color="primaryColor"
								:allow-opacity="false"
								:use-buttons="false"
								:color-library="[]"
								heading="Primary"
								placement="bottom"
								emit-format="hex"
								@change="color => { primaryColor = color; hasUnsavedColorChanges = true }"
							/>
							<span class="ml-2 font-bold">Primary</span>
						</div>
						<div class="flex items-center flex-shrink-0">
							<CColorPicker
								:color="secondaryColor"
								:allow-opacity="false"
								:use-buttons="false"
								:color-library="[]"
								heading="Secondary"
								placement="bottom"
								emit-format="hex"
								@change="color => { secondaryColor = color; hasUnsavedColorChanges = true }"
							/>
							<span class="ml-2 font-bold">Secondary</span>
						</div>
						<div class="flex items-center flex-shrink-0">
							<CColorPicker
								:color="importantColor"
								:allow-opacity="false"
								:use-buttons="false"
								:color-library="[]"
								heading="Important"
								placement="bottom"
								emit-format="hex"
								@change="color => { importantColor = color; hasUnsavedColorChanges = true }"
							/>
							<span class="ml-2 font-bold">Important</span>
						</div>
					</div>
					<div
						class="organization-appearance__thumbnail color-customization-thumbnail"
						:style="`
							--text: ${textColor};
							--background: ${backgroundColor};
							--primary: ${primaryColor};
							--secondary: ${secondaryColor};
							--important: ${importantColor};
							--derived-text-for-primary: ${derivedColors.derivedTextForPrimary};
							--derived-text-for-important: ${derivedColors.derivedTextForImportant};
							--derived-primary-text-for-background: ${derivedColors.derivedPrimaryTextForBackground};
							--derived-primary-icon-for-background: ${derivedColors.derivedPrimaryIconForBackground};
						`"
					>
						<OrganizationAppearanceThumbnail :logo-url="globalStore.orgAppearanceSettings?.LogoLink" />
					</div>
				</div>
				<div class="flex mt-4">
					<CIconButton
						v-if="!isDefault && !isApplyingChanges"
						icon="reset"
						:intense="false"
						text="Reset to Default"
						class="-ml-2"
						@click="resetToDefault"
					/>
					<div id="organization-appearance-controls" class="ml-auto">
						<CButton
							v-if="hasChanges && !isApplyingChanges"
							class="button button--secondary align-self-center mr-2"
							@click="resetToCurrent"
						>
							Cancel
						</CButton>
						<CStatusButton
							class="button button--primary align-self-center"
							:status="!hasChanges || !isACompliantText ? 'disabled' : (isApplyingChanges ? 'validating' : 'default')"
							@click="handleApplyColors"
						>
							Apply Colors
						</CStatusButton>
					</div>
				</div>
			</section>
			<section class="pb-5">
				<h2 class="font-display mb-4">Header Typeface</h2>
				<CCheckbox
					:value="useDefaultTypeface"
					:readonly="showUpsell"
					@click="handleTypfaceClicked"
				>
					<span class="select-none">Use the default typeface of the user's OS for headers</span>
				</CCheckbox>
			</section>
			<CUpsellDialog
				:show="showUpsellDialog"
				feature="orgappearance"
				:is-org-owner="isOrgOwner"
				:org-code="globalStore.organization.Code"
				@close="showUpsellDialog = false"
			/>
		</template>
	</div>
</template>
<script lang="ts">
	import { defineComponent, ref, inject, computed, onMounted, nextTick, watch } from 'vue';
	import CCheckbox from 'src/components/form-controls/Checkbox.vue';
	import CCallout from 'src/components/content/Callout.vue';
	import CIconButton from 'src/components/interaction/IconButton.vue';
	import CButton from 'src/components/interaction/Button.vue';
	import CStatusButton from 'src/components/interaction/StatusButton.vue';
	import CColorPicker from '../../../components/form-controls/ColorPicker.vue';
	import CUpsellDialog from 'src/components/dialogs/UpsellDialog.vue';
	import { globalStore } from 'src/stores/global-store';
	import OrganizationLogoUpload from 'src/views/settings/components/OrganizationLogoUpload.vue';
	import { isMobileKey } from 'src/utilities/injection-keys';
	import { setOrgAppearanceColors, setOrgDefaultTypeface } from '@cognitoforms/api/services/settings/org-appearance-service';
	import type { OrgCustomColors } from '@cognitoforms/types/server-types/model/org-custom-colors';
	import notify from 'src/utilities/notify';
	import { invalidateCache } from '@cognitoforms/utils/css-helper';
	import OrganizationAppearanceThumbnail from './OrganizationAppearanceThumbnail.vue';
	import { isDefaultColors, getDefaults, isAACompliant, isCustomCompliant, getDerivedColors, updateCustomTheme, updateTypeface } from 'src/views/admin/utils/org-appearance-utils';
	import { checkRole } from 'src/views/admin/utils/role-check';
	import { ClientRole } from '@cognitoforms/types/server-types/core/services/client-role';
	import session from '@cognitoforms/api/services/session';

	export default defineComponent({
		components: {
			CCheckbox,
			CCallout,
			CIconButton,
			CButton,
			CStatusButton,
			CColorPicker,
			CUpsellDialog,
			OrganizationLogoUpload,
			OrganizationAppearanceThumbnail
		},
		setup() {
			const loading = ref(true);

			const showUpsell = computed(() => !globalStore.organization.Plan?.AllowManageOrgAppearance);
			const showUpsellDialog = ref(false);
			const hasUnsavedColorChanges = ref(false);
			const isOrgOwner = computed(() => checkRole(ClientRole.Owner));
			const upsellText = computed(() => isOrgOwner.value ?
				'Upgrade to Enterprise to save your appearance settings.'
				: 'This feature requires a higher plan level. Please ask the organization owner to upgrade.');
			// Logo upload disabled if the org is on a trial with no card on file
			const isLogoDisabled = computed(() => globalStore.organization?.TrialDaysRemaining !== 0 && !globalStore.organization?.TrialWithCardOnFile);

			// Default typeface
			const useDefaultTypeface = ref(globalStore.orgAppearanceSettings?.UseDefaultTypeface ?? false);

			const handleTypfaceClicked = async () => {
				if (showUpsell.value)
					showUpsellDialog.value = true;
				else {
					useDefaultTypeface.value = !useDefaultTypeface.value;
					if (globalStore.orgAppearanceSettings?.UseDefaultTypeface !== useDefaultTypeface.value)
						await setOrgDefaultTypeface(useDefaultTypeface.value).then(() => {
							updateTypeface(useDefaultTypeface.value);
							notify('Successfully updated header typeface.', 'success');
						}).catch(() => {
							notify('Failed to update header typeface, please try again later', 'error');
						});
				}
			};

			watch(() => globalStore.orgAppearanceSettings?.UseDefaultTypeface, () => {
				useDefaultTypeface.value = globalStore.orgAppearanceSettings?.UseDefaultTypeface;
			});

			// Custom color
			const defaultColors = getDefaults();

			const textColor = ref(globalStore.orgAppearanceSettings?.Colors?.Text ?? defaultColors.Text);
			const backgroundColor = ref(globalStore.orgAppearanceSettings?.Colors?.Background ?? defaultColors.Background);
			const primaryColor = ref(globalStore.orgAppearanceSettings?.Colors?.Primary ?? defaultColors.Primary);
			const secondaryColor = ref(globalStore.orgAppearanceSettings?.Colors?.Secondary ?? defaultColors.Secondary);
			const importantColor = ref(globalStore.orgAppearanceSettings?.Colors?.Important ?? defaultColors.Important);

			const colors = computed(() => {
				return {
					Text: textColor.value,
					Background: backgroundColor.value,
					Primary: primaryColor.value,
					Secondary: secondaryColor.value,
					Important: importantColor.value
				} as OrgCustomColors;
			});

			// The color-contrast-checker package did not have a isLevelA checker
			// Using 5:2 ratio for Level A compliance
			const isACompliantText = computed(() => isCustomCompliant(textColor.value, backgroundColor.value, 5 / 2));
			const isAACompliantText = computed(() => isAACompliant(textColor.value, backgroundColor.value));

			const isApplyingChanges = ref(false);

			const hasChanges = computed(() => {
				return textColor.value !== globalStore.orgAppearanceSettings?.Colors?.Text
					|| backgroundColor.value !== globalStore.orgAppearanceSettings?.Colors?.Background
					|| primaryColor.value !== globalStore.orgAppearanceSettings?.Colors?.Primary
					|| secondaryColor.value !== globalStore.orgAppearanceSettings?.Colors?.Secondary
					|| importantColor.value !== globalStore.orgAppearanceSettings?.Colors?.Important;
			});

			const isDefault = computed(() => {
				return isDefaultColors(colors.value);
			});

			const derivedColors = computed(() => getDerivedColors(colors.value));

			const handleApplyColors = async () => {
				if (showUpsell.value)
					showUpsellDialog.value = true;
				else {
					isApplyingChanges.value = true;
					await setOrgAppearanceColors(colors.value, !isDefault.value).then(() => {
						invalidateCache();
						updateCustomTheme(session.value?.enabledFeatures.UseSystemColors, colors.value, null);
						notify('Successfully updated colors.', 'success');
						hasUnsavedColorChanges.value = false;
					}).catch(() => {
						notify('Failed to update colors, please try again later', 'error');
					});
				}
			};

			const resetToDefault = () => {
				textColor.value = defaultColors.Text;
				backgroundColor.value = defaultColors.Background;
				primaryColor.value = defaultColors.Primary;
				secondaryColor.value = defaultColors.Secondary;
				importantColor.value = defaultColors.Important;
				hasUnsavedColorChanges.value = false;
			};

			const resetToCurrent = () => {
				textColor.value = globalStore.orgAppearanceSettings?.Colors?.Text;
				backgroundColor.value = globalStore.orgAppearanceSettings?.Colors?.Background;
				primaryColor.value = globalStore.orgAppearanceSettings?.Colors?.Primary;
				secondaryColor.value = globalStore.orgAppearanceSettings?.Colors?.Secondary;
				importantColor.value = globalStore.orgAppearanceSettings?.Colors?.Important;
				hasUnsavedColorChanges.value = false;
			};

			watch(() => globalStore.orgAppearanceSettings?.Colors, () => {
				if (!hasUnsavedColorChanges.value) {
					resetToCurrent();
					isApplyingChanges.value = false;
				}
			});

			onMounted(async () => {
				globalStore.isReady.then(async () => {
					nextTick(() => {
						loading.value = false;
					});
				});
			});

			return {
				loading,
				globalStore,
				useDefaultTypeface,
				handleTypfaceClicked,
				showUpsell,
				showUpsellDialog,
				isOrgOwner,
				upsellText,
				isMobile: inject(isMobileKey),
				textColor,
				backgroundColor,
				primaryColor,
				secondaryColor,
				importantColor,
				derivedColors,
				hasChanges,
				hasUnsavedColorChanges,
				isApplyingChanges,
				isDefault,
				isAACompliantText,
				isACompliantText,
				resetToDefault,
				resetToCurrent,
				handleApplyColors,
				isLogoDisabled
			};
		}
	});
</script>
<style lang="scss">
.organization-appearance {
	--content-max-width: 650px;

	&__swatches > div + div {
		// To get the swatches to come closer to reaching to the bottom of the thumbnail.
		margin-top: 21px;
	}

	&__thumbnail {

		.i-appearance-thumbnail,
		.organization-appearance__skeleton-thumbnail {
			@media (max-width: $b9) {
				height: 170px;
			}

			@media (max-width: 699px) {
				height: 210px;
			}
			height: 210px;
		}
	}

	&__skeleton {

		&-logoicon-heading {
			width: 95px;
			height: 24px;
		}

		&-logoicon {
			width: 150px;
			height: 150px;
		}

		&-colors-heading {
			width: 68px;
			height: 24px;
		}

		&-swatch {
			width: 26px;
			height: 26px;

			&-text {
				width: 32px;
				height: 18px;
			}

			&-bg {
				width: 80px;
				height: 18px;
			}

			&-primary {
				width: 52px;
				height: 18px;
			}

			&-secondary {
				width: 69px;
				height: 18px;
			}

			&-important {
				width: 68px;
				height: 18px;
			}
		}

		&-thumbnail {
			width: 350px;
			height: 205px;
		}

		&-button {
			width: 105px;
			height: 34px;
		}

		&-typeface-heading {
			width: 160px;
			height: 24px;
		}

		&-checkbox {
			width: 18px;
			height: 18px;

			&-label {
				width: 323px;
				height: 18px;
			}
		}
	}
}
</style>